import * as React from 'react'
import { useCallback, useState } from 'react'

import { WorkflowIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { linkedWorkflowLikeFieldTypes } from '../../constants/PermitFieldTypeConstants'
import Localization from '../../localization/LocalizationManager'
import Workflow from '../../models/Workflow'
import PermitInspectionChangesStore from '../../stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '../../stores/domain/PermitInspections.store'
import WorkflowsStore from '../../stores/domain/Workflows.store'
import WorkflowTypesStore from '../../stores/domain/WorkflowTypes.store'
import EventsStore from '../../stores/EventStore/Events.store'
import { Button } from '../Button/Button'
import { CommonDateFilterMode } from '../CommonDateFilter'
import { ListSelector } from '../ListSelector'
import { Drawer, DrawerContent, DrawerTitle } from '../shadcn-ui/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '../shadcn-ui/popover'
import TooltipWrapper from '../TooltipWrapper/TooltipWrapper'
import WorkflowDirectory from '../WorkflowDirectory/WorkflowDirectory'

import './WorkflowBacklinks.scss'

interface IProps {
  workflow: Workflow
  workflowsStore?: WorkflowsStore
  workflowTypesStore?: WorkflowTypesStore
  permitInspectionsStore?: PermitInspectionsStore
  permitInspectionChangesStore?: PermitInspectionChangesStore
  eventsStore?: EventsStore
  isMobileEnv?: boolean
  className?: string
  showHeader?: boolean
  alignRight?: boolean

  onWorkflowClick?: (workflow: Workflow) => void
  displayOnly?: boolean
}

const WorkflowBacklinksComponent: React.FC<IProps> = ({
  workflow,
  permitInspectionsStore,
  permitInspectionChangesStore,
  isMobileEnv,
  className,
  onWorkflowClick,
  displayOnly,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const inspections =
    permitInspectionsStore?.getInspectionsForPermit(workflow.id) ?? []
  const inspectionLinkedIds = inspections.flatMap(i => {
    const latestChange =
      permitInspectionChangesStore?.getLatestChangeForInspection(i.id)
    const effective = latestChange?.toInspection(i.permitId) || i
    return linkedWorkflowLikeFieldTypes
      .flatMap(t => effective.getAllFieldsByType(t))
      .flatMap(f => (f.values as string[]) ?? [])
  })
  const linkedWorkflowIds = [
    ...new Set([...workflow.allLinkedWorkflowIds, ...inspectionLinkedIds]),
  ]

  const handleWorkflowClick = useCallback(
    (clickedWorkflow: Workflow) => {
      onWorkflowClick?.(clickedWorkflow)
      setIsOpen(false)
    },
    [onWorkflowClick],
  )

  const handleTriggerClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setIsOpen(prev => !prev)
    },
    [],
  )

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  if (!linkedWorkflowIds.length) {
    return null
  }

  const tooltipContent = `${linkedWorkflowIds.length} ${Localization.translator.linkedWorkflows}`

  const triggerContent = (
    <>
      {linkedWorkflowIds.length}
      <WorkflowIcon data-icon="inline-end" className="tw:size-3.5" />
    </>
  )

  const triggerButtonProps = {
    variant: 'primary-ghost',
    size: 'xs',
    className: 'tw:ml-1',
    'aria-label': tooltipContent,
  } as const

  if (displayOnly) {
    return (
      <div
        className={classList({
          'workflow-backlinks relative inline-block': true,
          [className]: !!className,
        })}
      >
        <TooltipWrapper content={tooltipContent}>
          <span className="tw:ml-1 tw:inline-flex tw:items-center tw:gap-1 tw:text-app-dense-medium tw:text-primary">
            {triggerContent}
          </span>
        </TooltipWrapper>
      </div>
    )
  }

  const directoryContent = (
    <>
      <ListSelector.Title
        title={Localization.translator.linkedWorkflows}
        onClose={handleClose}
      />
      <WorkflowDirectory
        isReadOnly
        restrictToIds={linkedWorkflowIds}
        onWorkflowClick={handleWorkflowClick}
        onCancel={handleClose}
        allowCreateNew={false}
        showQuickFilters={true}
        displayOnlyBacklinks
        initialDateFilterMode={CommonDateFilterMode.PROJECT}
      />
    </>
  )

  return (
    <div
      className={classList({
        'workflow-backlinks relative inline-block': true,
        [className]: !!className,
      })}
    >
      {isMobileEnv ? (
        <>
          <TooltipWrapper content={tooltipContent}>
            <Button {...triggerButtonProps} onClick={handleTriggerClick}>
              {triggerContent}
            </Button>
          </TooltipWrapper>
          {/* A real drawer, so the list's own Filter / Display drawers stack
              above it; noBodyStyles keeps the full-height map layout intact. */}
          <Drawer open={isOpen} onOpenChange={setIsOpen} noBodyStyles>
            <DrawerContent
              // The drawer's own max height (80vh) as a fixed height: the
              // virtualized list inside measures its box.
              className="tw:h-[80vh]"
              aria-describedby={undefined}
            >
              <DrawerTitle className="tw:sr-only">
                {Localization.translator.linkedWorkflows}
              </DrawerTitle>
              <div className="workflow-backlinks-mobile-directory tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
                {directoryContent}
              </div>
            </DrawerContent>
          </Drawer>
        </>
      ) : (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <TooltipWrapper content={tooltipContent}>
            <PopoverTrigger
              render={
                <Button {...triggerButtonProps} onClick={stopPropagation} />
              }
            >
              {triggerContent}
            </PopoverTrigger>
          </TooltipWrapper>
          <PopoverContent
            backdrop
            side="top"
            align="end"
            className="tw:w-auto tw:gap-0 tw:overflow-hidden tw:p-0"
          >
            <div className="workflow-backlinks-directory">
              {directoryContent}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

const WorkflowBacklinks = inject(
  'eventsStore',
  'workflowsStore',
  'workflowTypesStore',
  'permitInspectionsStore',
  'permitInspectionChangesStore',
  'isMobileEnv',
)(observer(WorkflowBacklinksComponent))

export default WorkflowBacklinks
